import React from "react";

import {
  FmMapObservationContext,
  type FmMapRadarCopy,
  type FmMapRadarPlaybackModel,
} from "./FmMapObservationContext";
import type { FmMapRadarDescriptor } from "./FmMapWorkspaceRoot.types";

const PLAYBACK_INTERVAL_MS = 750;
const NOOP = (): void => undefined;

function interpolate(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = values[key];
    return value == null ? match : String(value);
  });
}

function summaryText(
  snapshot: FmMapRadarDescriptor,
  copy: FmMapRadarCopy,
): string | null {
  if (snapshot.affectedStandCount.status !== "available") return null;
  const count = snapshot.affectedStandCount.value;
  if (snapshot.maximumIntensity.status !== "available") {
    return count > 0
      ? interpolate(copy.affectedStands, { count })
      : copy.noStandOverlap;
  }
  const max = snapshot.maximumIntensity.value.value.toFixed(1);
  // Accumulated rain (AE1's rainfall-deficit signal) is additive: when the
  // radar summary carries it, the status text names the actual accumulated
  // total instead of leaving it an unconsumed, invisible field.
  if (snapshot.accumulatedRain.status === "available") {
    return interpolate(copy.affectedSummaryWithRain, {
      count,
      max,
      rain: snapshot.accumulatedRain.value.value.toFixed(1),
    });
  }
  return interpolate(copy.affectedSummary, { count, max });
}

export const FmMapObservationProvider: React.FC<{
  children: React.ReactNode;
  enabled: boolean;
  snapshot: FmMapRadarDescriptor;
}> = ({ children, enabled, snapshot }) => {
  const copy = snapshot.copy;
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [opacity, setOpacity] = React.useState(72);
  const [playing, setPlaying] = React.useState(false);
  const propertyId = snapshot.propertyId;
  const frames = snapshot.frames;
  const timelineIdentity = frames
    .map((frame) => `${frame.id}\u001f${frame.capturedAt}`)
    .join("\u001e");
  const activeFrame = frames[activeIndex] ?? null;
  const layerStatus =
    activeFrame?.render.status === "unavailable"
      ? activeFrame.render.reason.replace(/_/g, " ")
      : null;
  const noData = snapshot.state === "empty" || frames.length === 0;

  React.useEffect(() => {
    setActiveIndex(0);
    setPlaying(false);
  }, [propertyId, timelineIdentity]);

  React.useEffect(() => {
    if (!enabled || !playing || frames.length < 2) return undefined;
    const interval = window.setInterval(
      () => setActiveIndex((current) => (current + 1) % frames.length),
      PLAYBACK_INTERVAL_MS,
    );
    return () => window.clearInterval(interval);
  }, [enabled, frames.length, playing]);

  React.useEffect(() => {
    if (!enabled || frames.length === 0) setPlaying(false);
  }, [enabled, frames.length]);

  const statusText = React.useMemo(() => {
    if (!propertyId) return copy.selectProperty;
    if (snapshot.state === "loading") return copy.loadingTimeline;
    if (snapshot.error) return snapshot.error;
    if (noData)
      return snapshot.noDataReason?.replace(/_/g, " ") ?? copy.noFrames;
    if (layerStatus)
      return interpolate(copy.frameUnavailable, { reason: layerStatus });
    if (snapshot.summaryError) return snapshot.summaryError;
    return summaryText(snapshot, copy) ?? copy.loaded;
  }, [copy, layerStatus, noData, propertyId, snapshot]);

  const value = React.useMemo<FmMapRadarPlaybackModel>(
    () => ({
      activeFrame,
      activeIndex,
      canStep: frames.length > 1,
      copy,
      enabled,
      error: snapshot.error,
      frameCount: frames.length,
      layerStatus,
      loading: snapshot.state === "loading",
      noData,
      opacity,
      playing,
      propertyId,
      reload: snapshot.reload ?? NOOP,
      setOpacity,
      setPlaying,
      statusText,
      windowEnd: snapshot.window?.end ?? null,
      windowStart: snapshot.window?.start ?? null,
      stepBackward: () => setActiveIndex((current) => Math.max(0, current - 1)),
      stepForward: () =>
        setActiveIndex((current) =>
          Math.min(Math.max(frames.length - 1, 0), current + 1),
        ),
    }),
    [
      activeFrame,
      activeIndex,
      copy,
      enabled,
      frames.length,
      layerStatus,
      noData,
      opacity,
      playing,
      propertyId,
      snapshot,
      statusText,
    ],
  );

  return (
    <FmMapObservationContext.Provider value={value}>
      {children}
    </FmMapObservationContext.Provider>
  );
};
